import { MaterialCommunityIcons } from "@expo/vector-icons";
import { Box, Button, Center, HStack, Icon, Pressable, Text, VStack } from "native-base";
import React, { memo, useEffect, useState } from "react";

import ExternalImage from "@/components/atoms/ExternalImage";
import { useAppSelector } from "@/hooks/application/store";
import { OrderItem } from "@/interfaces/domain/Order";
import { image_path_products_card } from "@/services/api";
import { adjustCaseText, formatToCurrency, formatWeight } from "@/utils/helpers";

type ProductCardProps = {
  item: OrderItem;
  variables: {
    quantity: number;
    total: number;
  };
  onSelect: () => void;
  onPressIncrease: () => void;
  onPressDecrease: () => void;
};

const ProductCardContent = memo(
  ({ item, variables, onPressDecrease, onPressIncrease, onSelect }: ProductCardProps) => {
    const product = item.item;

    const productPrice = product.preco;
    const productImage = product?.fotos_produto?.length
      ? product?.fotos_produto[0]
      : product?.foto_produto;
    const imageUrl = `${image_path_products_card}/${productImage}`;

    const isUnity = product.tipo_embalagem !== "kg";
    const isSale = !!productPrice.preco_encarte;

    return (
      <Pressable
        w={{
          base: "170px",
          lg: "200px",
        }}
        h="340px"
        rounded="lg"
        shadow="4"
        bgColor="white.50"
        m="2"
        justifyContent="flex-end"
        onPress={onSelect}>
        <Box position="absolute" top="6px" w="full">
          <Center>
            <ExternalImage
              width={125}
              height={125}
              source={{ uri: imageUrl }}
              contentFit="contain"
            />
          </Center>
        </Box>
        {isSale && (
          <Box position="absolute" top={0} bgColor="secondary.400" p="1" rounded="lg">
            <Text fontSize="xs" textTransform="uppercase" color="primary.600" bold>
              Oferta
            </Text>
          </Box>
        )}
        <VStack p="2">
          <Text fontSize="sm" textTransform="capitalize" color="text.600" bold numberOfLines={2}>
            {adjustCaseText(product.nome)}
          </Text>
          <HStack py="2" space="5px" alignItems="center" flexWrap="wrap">
            <Text fontSize="lg" color="primary.600" bold>
              {isUnity
                ? formatToCurrency(item.preco_unitario)
                : formatToCurrency(item.preco_unitario / product.peso) + "/Kg"}
            </Text>
            {isSale && (
              <Text
                fontSize={{
                  base: "xs",
                  lg: "sm",
                }}
                strikeThrough
                color="gray.600"
                bold>
                {isUnity
                  ? formatToCurrency(productPrice.preco_original)
                  : formatToCurrency(productPrice.preco_original / product.peso) + "/Kg"}
              </Text>
            )}
          </HStack>
          <VStack
            mb={{
              base: 0,
              lg: 4,
            }}
            py="2">
            {variables.quantity > 0 ? (
              <HStack justifyContent="space-between" alignItems="center">
                <Text fontSize="md">{formatToCurrency(variables.total)}</Text>
                <Icon as={<MaterialCommunityIcons name="check" />} color="green.600" size="lg" />
              </HStack>
            ) : isUnity ? (
              <Text fontSize="md">1 und.</Text>
            ) : (
              <Text fontSize="md">
                {formatToCurrency(item.preco_unitario)} ≅ {formatWeight(product.peso)}
              </Text>
            )}
          </VStack>
          <VStack>
            {variables.quantity === 0 ? (
              <Button
                my="2"
                variant="solid"
                colorScheme="white"
                _text={{ color: "primary.600", bold: true }}
                shadow="6"
                onPress={onPressIncrease}>
                Adicionar
              </Button>
            ) : (
              <HStack my="2" justifyContent="space-between" alignItems="center">
                <Button variant="solid" colorScheme="white" shadow="6" onPress={onPressDecrease}>
                  <Icon
                    as={<MaterialCommunityIcons name="minus-thick" />}
                    size="md"
                    color="primary.600"
                  />
                </Button>
                <Text fontSize="md" bold textAlign="center">
                  {isUnity
                    ? `${variables.quantity} und.`
                    : `≅ ${formatWeight(product.peso * variables.quantity)}`}
                </Text>
                <Button variant="solid" colorScheme="white" shadow="6" onPress={onPressIncrease}>
                  <Icon
                    as={<MaterialCommunityIcons name="plus-thick" />}
                    size="md"
                    color="primary.600"
                  />
                </Button>
              </HStack>
            )}
          </VStack>
        </VStack>
      </Pressable>
    );
  }
);

const ProductCard = ({ item, ...props }: Omit<ProductCardProps, "variables">) => {
  const items = useAppSelector((store) => store.checkout.items);
  const [variables, setVariables] = useState({
    quantity: 0,
    total: 0,
  });

  useEffect(() => {
    const existentItem = items.get(item.item._id);
    if (existentItem) {
      setVariables({
        quantity: existentItem.quantidade,
        total: existentItem.preco_total,
      });
    } else {
      if (variables.quantity !== 0) {
        setVariables({
          quantity: 0,
          total: 0,
        });
      }
    }
  }, [items]);

  return <ProductCardContent item={item} variables={variables} {...props} />;
};

export default memo(ProductCard);
